/**
 * MediaAlbum/actions.js
 * ════════════════════════════════════════════════════════════════
 * API для роботи з альбомами галереї.
 * Завантаження файлів — через існуючий uploadFile з documents/actions.js
 * (PUT /v1/docfile з doc_id = album._id).
 * ════════════════════════════════════════════════════════════════
 */

import apiClient, {
    apiSendFileHeader,
    loadFileAsBlob,
} from '../../../../../services/apiClient';

// ─── Альбоми (адмін) ─────────────────────────────────────────────────────────

export const listAlbums = async (kgId) => {
    const response = await apiClient.get(`kg_albums?kg_id=${kgId}`);
    return response.data || response || [];
};

export const getAlbum = async (albumId) => {
    const response = await apiClient.get(`kg_album?id=${albumId}`);
    return response.data || response;
};

export const createAlbum = async (data) => {
    // data: { kg_id, name, description, category, group_ids[], event_date, is_public }
    const response = await apiClient.post('kg_album', data);
    return response.data || response;
};

export const updateAlbum = async (albumId, data) => {
    const response = await apiClient.put(`kg_album?id=${albumId}`, data);
    return response.data || response;
};

export const setAlbumPublic = async (albumId, isPublic) => {
    const response = await apiClient.put(
        `kg_album_public?id=${albumId}&value=${isPublic}`
    );
    return response.data || response;
};

export const deleteAlbum = async (albumId) => {
    const response = await apiClient.delete(`kg_album?id=${albumId}`);
    return response.data || response;
};

// ─── Файли в альбомі ─────────────────────────────────────────────────────────

/**
 * Завантажити файл в альбом.
 * Використовує той самий PUT /v1/docfile що й документи —
 * бекенд розпізнає type='media_album' і викликає addGalleryFile.
 */
export const uploadAlbumFile = async (albumId, file, meta) => {
    const m = meta || {};

    const fileInfo = {
        doc_id:   albumId,
        name:     file.name,
        mtype:    file.type,
        length:   file.size,
        date:     new Date().toISOString(),
        caption:  m.caption || '',
        // Категорія і група беруться з альбому — не з файлу
    };

    const buffer = await readFileAsBuffer(file);
    const response = await apiSendFileHeader('docfile', buffer, fileInfo);
    return response;
};

/**
 * Оновити підпис файлу.
 */
export const updateFileCaption = async (albumId, fileId, caption) => {
    const response = await apiClient.put(
        `kg_album_file_caption?album_id=${albumId}&file_id=${fileId}`,
        { caption }
    );
    return response.data || response;
};

/**
 * Змінити публічність окремого файлу.
 * Реиспользуємо існуючий ендпоінт.
 */
export const setFilePublic = async (albumId, fileId, isPublic) => {
    const response = await apiClient.put(
        `file_set_public?doc_id=${albumId}&file_id=${fileId}&value=${isPublic}`
    );
    return response.data || response;
};

/**
 * Перемістити файл в кошик.
 */
export const fileToTrash = async (albumId, fileId) => {
    const response = await apiClient.post('file_to_trash', {
        id_doc: albumId,
        id_file: fileId,
    });
    return response.data || response;
};

/**
 * Отримати blob файлу для FileViewer.
 */
export const getFileBlob = async (albumId, fileId) => {
    return await loadFileAsBlob(`docfile?doc_id=${albumId}&file_id=${fileId}`);
};

// ─── YouTube в альбомі ───────────────────────────────────────────────────────

/**
 * Отримати метадані відео (title, thumbnail, duration).
 * Бекенд проксує до YouTube Data API або oEmbed.
 */
export const getYoutubeInfo = async (url) => {
    const response = await apiClient.get(
        `youtube_info?url=${encodeURIComponent(url)}`
    );
    return response.data || response;
};

/**
 * Зберегти YouTube-відео до альбому.
 * Бекенд викликає addYoutubeLink → зберігає як files[]{source:'youtube'}.
 */
export const addAlbumYoutube = async (albumId, youtubeUrl, meta) => {
    const m = meta || {};
    const response = await apiClient.post('youtube_link', {
        id_doc:     albumId,
        url:        youtubeUrl,
        caption:    m.caption    || '',
    });
    return response.data || response;
};

/**
 * Видалити YouTube-запис з альбому (soft delete через fileRemove).
 */
export const removeAlbumYoutube = async (albumId, fileId) => {
    const response = await apiClient.post('file_remove', {
        id_doc:  albumId,
        id_file: fileId,
    });
    return response.data || response;
};

// ─── Публічні ─────────────────────────────────────────────────────────────────

export const listPublicAlbums = async (kgId, filters) => {
    const f = filters || {};
    let url = `public/kg_albums?kg_id=${kgId}`;
    if (f.groupId)   url += `&group_id=${f.groupId}`;
    if (f.category)  url += `&category=${f.category}`;
    const response = await apiClient.get(url);
    return response.data || response || [];
};

export const getPublicAlbum = async (albumId) => {
    const response = await apiClient.get(`public/kg_album?id=${albumId}`);
    return response.data || response;
};

// ─── Допоміжна ───────────────────────────────────────────────────────────────

const readFileAsBuffer = (file) =>
    new Promise(function(resolve, reject) {
        const reader = new FileReader();
        reader.onload  = function() { resolve(reader.result); };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });